import { Card, Col, Input, Row, Select, Space, Switch, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { CONTENT_PAGE_TYPES, ContentPageType, IContentPage } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';

const META_BOX_STYLE = {
  borderRadius: 8,
  padding: 12,
  background: '#fafafa' as const,
};

type TContentMetaForm = {
  pageType: ContentPageType;
  title: string;
  tags: string[];
  onChange: (patch: Partial<Pick<IContentPage, 'pageType' | 'title' | 'tags'>>) => void;
  localeTitles?: Partial<Record<SupportedLocales, string>>;
  onLocaleTitleChange?: (locale: SupportedLocales, value: string) => void;
  localeDescriptions?: Partial<Record<SupportedLocales, string>>;
  onLocaleDescriptionChange?: (locale: SupportedLocales, value: string) => void;
  hideTags?: boolean;
  hidePageType?: boolean;
  published?: boolean;
  isPinned?: boolean;
  onPublishedChange?: (value: boolean) => void;
  onPinnedChange?: (value: boolean) => void;
  titleRequired?: boolean;
  invalidNameLocales?: SupportedLocales[];
};

const RequiredMark = () => (
  <span style={{ color: '#ff4d4f', marginLeft: 4 }} aria-hidden>
    *
  </span>
);

const ContentMetaForm = ({
  pageType,
  title,
  tags,
  onChange,
  localeTitles,
  onLocaleTitleChange,
  localeDescriptions,
  onLocaleDescriptionChange,
  hideTags = false,
  hidePageType = false,
  published,
  isPinned,
  onPublishedChange,
  onPinnedChange,
  titleRequired = false,
  invalidNameLocales = [],
}: TContentMetaForm) => {
  const { t } = useTranslation(['contentPages']);

  const pageTypeOptions = CONTENT_PAGE_TYPES.map((value) => ({
    value,
    label: t(`page_type_${value}`),
  }));

  const showSwitches = onPublishedChange != null && onPinnedChange != null;

  const nameBlockInvalid = invalidNameLocales.length > 0;

  const localeTabLabel = (loc: SupportedLocales, invalidList: SupportedLocales[]) => {
    const bad = invalidList.includes(loc);
    return (
      <span style={{ color: bad ? '#ff4d4f' : undefined, fontWeight: bad ? 600 : undefined }}>{loc.toUpperCase()}</span>
    );
  };

  return (
    <Card title={t('meta_section_title')} style={{ marginBottom: 16 }}>
      {!hidePageType && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('field_page_type')}</div>
            <Select
              style={{ width: '100%' }}
              value={pageType}
              options={pageTypeOptions}
              onChange={(value) => onChange({ pageType: value as ContentPageType })}
            />
          </Col>
        </Row>
      )}
      <Row gutter={[24, 24]} align="middle">
        <Col xs={24} lg={showSwitches ? 14 : 24} style={{ minWidth: 0 }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>
                {t('field_title')}
                {titleRequired ? <RequiredMark /> : null}
              </div>
              {onLocaleTitleChange ? (
                <div
                  style={{
                    ...META_BOX_STYLE,
                    border: `1px solid ${nameBlockInvalid ? '#ff4d4f' : '#f0f0f0'}`,
                  }}
                >
                  <Tabs
                    size="small"
                    items={[
                      {
                        key: SupportedLocales.UK,
                        label: localeTabLabel(SupportedLocales.UK, invalidNameLocales),
                        children: (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Input
                              value={localeTitles?.[SupportedLocales.UK] ?? ''}
                              onChange={(e) => onLocaleTitleChange(SupportedLocales.UK, e.target.value)}
                              status={invalidNameLocales.includes(SupportedLocales.UK) ? 'error' : undefined}
                            />
                            {onLocaleDescriptionChange ? (
                              <>
                                <div style={{ fontWeight: 600 }}>{t('field_description_optional')}</div>
                                <Input.TextArea
                                  value={localeDescriptions?.[SupportedLocales.UK] ?? ''}
                                  onChange={(e) => onLocaleDescriptionChange(SupportedLocales.UK, e.target.value)}
                                  rows={3}
                                  maxLength={255}
                                  showCount
                                />
                              </>
                            ) : null}
                          </Space>
                        ),
                      },
                      {
                        key: SupportedLocales.RU,
                        label: localeTabLabel(SupportedLocales.RU, invalidNameLocales),
                        children: (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Input
                              value={localeTitles?.[SupportedLocales.RU] ?? ''}
                              onChange={(e) => onLocaleTitleChange(SupportedLocales.RU, e.target.value)}
                              status={invalidNameLocales.includes(SupportedLocales.RU) ? 'error' : undefined}
                            />
                            {onLocaleDescriptionChange ? (
                              <>
                                <div style={{ fontWeight: 600 }}>{t('field_description_optional')}</div>
                                <Input.TextArea
                                  value={localeDescriptions?.[SupportedLocales.RU] ?? ''}
                                  onChange={(e) => onLocaleDescriptionChange(SupportedLocales.RU, e.target.value)}
                                  rows={3}
                                  maxLength={255}
                                  showCount
                                />
                              </>
                            ) : null}
                          </Space>
                        ),
                      },
                    ]}
                  />
                </div>
              ) : (
                <Input value={title} onChange={(e) => onChange({ title: e.target.value })} />
              )}
            </div>
            {!hideTags && (
              <div>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('field_tags')}</div>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  value={tags}
                  placeholder={t('field_tags_placeholder')}
                  onChange={(value) => onChange({ tags: value })}
                />
              </div>
            )}
          </Space>
        </Col>
        {showSwitches ? (
          <Col xs={24} lg={10} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Space size="large" wrap align="center">
              <Space align="center" size={8}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t('field_published')}</span>
                <Switch checked={Boolean(published)} onChange={onPublishedChange} />
              </Space>
              <Space align="center" size={8}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t('field_is_pinned')}</span>
                <Switch checked={Boolean(isPinned)} onChange={onPinnedChange} />
              </Space>
            </Space>
          </Col>
        ) : null}
      </Row>
    </Card>
  );
};

export default ContentMetaForm;
